import { type UseQueryResult, useQueries } from '@tanstack/react-query';

import type { SwaggerResponse } from '@/services/config';
import { useLocationFishGroupsController_timeline } from '@/services/hooks';
import type { FishGroupTimelineDto } from '@/services/types';

import { mapSiteTimelineToDomain } from './siteTimeline.mappers';
import type { SiteTimeline, SiteTimelineScope } from './siteTimeline.types';
import { siteTimelineQueryParams } from './siteTimelineQuery';

export interface UseGetSiteTimelinesResult {
    /** One per scope that has loaded, in the order asked for. */
    timelines: SiteTimeline[];
    isLoading: boolean;
    error: Error | null;
    /** Retries the scopes that failed. */
    refetch: () => void;
}

// Module-level, so react-query keeps the combined result while the queries are unchanged.
function combineTimelines(
    results: UseQueryResult<SwaggerResponse<FishGroupTimelineDto>>[]
): UseGetSiteTimelinesResult {
    return {
        timelines: results.flatMap((result) =>
            result.data?.data ? [mapSiteTimelineToDomain(result.data.data)] : []
        ),
        isLoading: results.some((result) => result.isLoading),
        error: results.find((result) => result.error)?.error ?? null,
        refetch: () => {
            for (const result of results) {
                if (result.error) result.refetch();
            }
        },
    };
}

/**
 * Several scopes of one site's board at once, such as the selected stocking and the groups in
 * none, for a dialog that offers groups from both. Shares the board's cache entries.
 */
export const useGetSiteTimelines = (
    clientId: number,
    locationId: number,
    scopes: readonly SiteTimelineScope[]
): UseGetSiteTimelinesResult =>
    useQueries({
        queries: scopes.map((scope) => {
            const { key, fun } = useLocationFishGroupsController_timeline.info(
                clientId,
                locationId,
                siteTimelineQueryParams(scope)
            );
            return { queryKey: key, queryFn: fun };
        }),
        combine: combineTimelines,
    });
